import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProductsDropdownOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Portfolio',
    path: '/portfolio'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Team',
    path: '/team'
  }, {
    name: 'Support',
    path: '/support'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-black/80 backdrop-blur-md border-b border-white/10 py-4' : 'bg-transparent py-6'}`} data-id="element-429">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-430">
        <Link to="/" className="flex items-center gap-2 group" data-id="element-431">
          <img src="https://cdn.magicpatterns.com/uploads/tEKY1KVFn8NPQae4HonbJW/9fea8157-abe6-4116-8e53-72149f139631.png" alt="Design Flow Digitals" className="w-10 h-10 object-contain" data-id="element-432" />
          <span className="font-bold text-xl tracking-tight text-white group-hover:text-gradient-gold transition-all duration-300" data-id="element-433">
            Design Flow Digitals
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8" data-id="element-434">
          <Link to="/" className="text-sm font-medium text-gray-300 hover:text-white transition-colors" data-id="element-435">
            Home
          </Link>

          <div className="relative group" onMouseEnter={() => setIsProductsDropdownOpen(true)} onMouseLeave={() => setIsProductsDropdownOpen(false)} data-id="element-436">
            <button className="flex items-center gap-1 text-sm font-medium text-gray-300 hover:text-white transition-colors py-2" data-id="element-437">
              Shopify Apps <ChevronDownIcon className="w-4 h-4" data-id="element-438" />
            </button>

            <div className={`absolute top-full left-0 pt-2 w-56 transition-all duration-200 ${isProductsDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}`} data-id="element-439">
              <div className="glass-card p-2 flex flex-col gap-1" data-id="element-440">
                <Link to="/apps" className="px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-colors" data-id="element-441">
                  All Apps
                </Link>
                <div className="h-px bg-white/10 my-1 mx-2" data-id="element-442" />
                <Link to="/seo-suite" className="px-4 py-2 text-sm font-medium text-emerald-400 hover:bg-white/10 rounded-md transition-colors flex items-center justify-between" data-id="element-443">
                  SEO Suite{' '}
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-444" />
                </Link>
                <Link to="/ai-photoshoot" className="px-4 py-2 text-sm font-medium text-purple-400 hover:bg-white/10 rounded-md transition-colors flex items-center justify-between" data-id="element-445">
                  AI Photoshoot{' '}
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-446" />
                </Link>
              </div>
            </div>
          </div>

          {navLinks.slice(1).map(link => <Link key={link.name} to={link.path} className="text-sm font-medium text-gray-300 hover:text-white transition-colors" data-id="element-447">
              {link.name}
            </Link>)}
        </nav>

        <div className="hidden lg:block" data-id="element-448">
          <Link to="/contact" className="bg-gradient-gold text-black px-6 py-2.5 rounded-full font-semibold text-sm gold-glow-hover transition-all duration-300" data-id="element-449">
            Start Project
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-450">
          {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-451" /> : <MenuIcon className="w-6 h-6" data-id="element-452" />}
        </button>
      </div>

      {/* Mobile Nav Drawer */}
      <div className={`fixed inset-0 bg-black/95 backdrop-blur-xl z-40 lg:hidden transition-transform duration-300 pt-24 px-6 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-453">
        <nav className="flex flex-col gap-6 text-lg" data-id="element-454">
          <Link to="/" className="font-medium text-gray-300 hover:text-white" data-id="element-455">
            Home
          </Link>
          <div className="flex flex-col gap-4 border-l-2 border-white/10 pl-4 ml-2" data-id="element-456">
            <span className="text-sm text-gray-500 uppercase tracking-wider" data-id="element-457">
              Shopify Apps
            </span>
            <Link to="/apps" className="font-medium text-gray-300 hover:text-white" data-id="element-458">
              All Apps
            </Link>
            <Link to="/seo-suite" className="font-medium text-emerald-400 flex items-center gap-2" data-id="element-459">
              SEO Suite{' '}
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-460" />
            </Link>
            <Link to="/ai-photoshoot" className="font-medium text-purple-400 flex items-center gap-2" data-id="element-461">
              AI Photoshoot{' '}
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-462" />
            </Link>
          </div>
          {navLinks.slice(1).map(link => <Link key={link.name} to={link.path} className="font-medium text-gray-300 hover:text-white" data-id="element-463">
              {link.name}
            </Link>)}
          <Link to="/contact" className="mt-8 bg-gradient-gold text-black px-6 py-3 rounded-full font-semibold text-center gold-glow" data-id="element-464">
            Start Project
          </Link>
        </nav>
      </div>
    </header>;
}